import { ICE_LEVELS, SIZES, SUGAR_LEVELS, type CartLineInput, type IceLevel, type Size, type SugarLevel } from '@khaddi/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_DISTANCE_KM } from '../config';
import { useFeatures } from './appConfig';
import { isRecord, numberOr, readStored, stringOr, writeStored } from './storage';

export type AppChannel = 'app_delivery' | 'app_pickup';

export const CHANNEL_OPTIONS: { value: AppChannel; label: string; icon: 'bike' | 'store' }[] = [
  { value: 'app_delivery', label: 'Giao tận nơi', icon: 'bike' },
  { value: 'app_pickup', label: 'Tại quán', icon: 'store' },
];

/** A cart line plus what the cart needs to draw it before the first quote arrives. */
export type CartLine = CartLineInput & {
  key: string;
  name: string;
  tint: string;
  background: string;
  unitPrice: number;
  optionsLabel: string;
};

export type DeliveryDraft = { recipientName: string; phone: string; address: string; distanceKm: number; note: string };

type CartState = {
  lines: CartLine[];
  channel: AppChannel;
  branchId: string | null;
  delivery: DeliveryDraft;
  voucherCode: string | null;
};

type CartValue = CartState & {
  /** Channels the store has switched on in the console; `channel` is always one of them when any is on. */
  channels: AppChannel[];
  count: number;
  addLine: (line: Omit<CartLine, 'key'>) => void;
  setQuantity: (key: string, quantity: number) => void;
  removeLine: (key: string) => void;
  clearLines: () => void;
  setChannel: (channel: AppChannel) => void;
  setBranchId: (branchId: string) => void;
  updateDelivery: (patch: Partial<DeliveryDraft>) => void;
  setVoucherCode: (code: string | null) => void;
};

const KEY = 'khaddi.client.cart';
const MAX_QUANTITY = 20;

const EMPTY: CartState = {
  lines: [],
  channel: 'app_delivery',
  branchId: null,
  delivery: { recipientName: '', phone: '', address: '', distanceKm: DEFAULT_DISTANCE_KM, note: '' },
  voucherCode: null,
};

const pick = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T => allowed.find((option) => option === value) ?? fallback;

const parseLine = (raw: unknown): CartLine | null => {
  if (!isRecord(raw) || typeof raw.productId !== 'string' || typeof raw.key !== 'string') return null;
  const toppingIds = Array.isArray(raw.toppingIds) ? raw.toppingIds.filter((id): id is string => typeof id === 'string').slice(0, 3) : [];
  return {
    key: raw.key,
    productId: raw.productId,
    size: pick<Size>(SIZES, raw.size, 'M'),
    sugar: pick<SugarLevel>(SUGAR_LEVELS, raw.sugar, '70%'),
    ice: pick<IceLevel>(ICE_LEVELS, raw.ice, 'Bình thường'),
    toppingIds,
    quantity: Math.min(MAX_QUANTITY, Math.max(1, Math.round(numberOr(raw.quantity, 1)))),
    name: stringOr(raw.name, ''),
    tint: stringOr(raw.tint, '#C98A5B'),
    background: stringOr(raw.background, '#F6EADF'),
    unitPrice: numberOr(raw.unitPrice, 0),
    optionsLabel: stringOr(raw.optionsLabel, ''),
  };
};

/** Validates what came back from storage field by field; a bad entry never crashes the app. */
const restore = (): CartState => {
  const raw = readStored(KEY);
  if (!isRecord(raw)) return EMPTY;
  const delivery = isRecord(raw.delivery) ? raw.delivery : {};
  return {
    lines: Array.isArray(raw.lines) ? raw.lines.map(parseLine).filter((line): line is CartLine => line !== null) : [],
    channel: raw.channel === 'app_pickup' ? 'app_pickup' : 'app_delivery',
    branchId: typeof raw.branchId === 'string' ? raw.branchId : null,
    delivery: {
      recipientName: stringOr(delivery.recipientName, ''),
      phone: stringOr(delivery.phone, ''),
      address: stringOr(delivery.address, ''),
      distanceKm: numberOr(delivery.distanceKm, DEFAULT_DISTANCE_KM),
      note: stringOr(delivery.note, ''),
    },
    voucherCode: typeof raw.voucherCode === 'string' ? raw.voucherCode : null,
  };
};

/** Same drink with the same options is one line, so adding it again bumps the quantity. */
const sameChoice = (a: CartLineInput, b: CartLineInput): boolean =>
  a.productId === b.productId && a.size === b.size && a.sugar === b.sugar && a.ice === b.ice && [...a.toppingIds].sort().join() === [...b.toppingIds].sort().join();

const CartContext = createContext<CartValue | null>(null);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<CartState>(restore);
  const { inStoreOrdering, delivery } = useFeatures();
  const channels = useMemo(
    () => CHANNEL_OPTIONS.map((o) => o.value).filter((c) => (c === 'app_pickup' ? inStoreOrdering : delivery)),
    [inStoreOrdering, delivery],
  );
  const channel = channels.includes(state.channel) ? state.channel : (channels[0] ?? state.channel);

  useEffect(() => writeStored(KEY, state), [state]);

  const addLine = useCallback((line: Omit<CartLine, 'key'>) => {
    setState((s) => {
      const existing = s.lines.find((l) => sameChoice(l, line));
      if (existing) {
        return { ...s, lines: s.lines.map((l) => (l === existing ? { ...l, quantity: Math.min(MAX_QUANTITY, l.quantity + line.quantity) } : l)) };
      }
      return { ...s, lines: [...s.lines, { ...line, key: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}` }] };
    });
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    setState((s) => ({ ...s, lines: s.lines.map((l) => (l.key === key ? { ...l, quantity: Math.min(MAX_QUANTITY, Math.max(1, quantity)) } : l)) }));
  }, []);

  const removeLine = useCallback((key: string) => setState((s) => ({ ...s, lines: s.lines.filter((l) => l.key !== key) })), []);
  const clearLines = useCallback(() => setState((s) => ({ ...s, lines: [], voucherCode: null })), []);
  const setChannel = useCallback((channel: AppChannel) => setState((s) => ({ ...s, channel })), []);
  const setBranchId = useCallback((branchId: string) => setState((s) => ({ ...s, branchId })), []);
  const updateDelivery = useCallback((patch: Partial<DeliveryDraft>) => setState((s) => ({ ...s, delivery: { ...s.delivery, ...patch } })), []);
  const setVoucherCode = useCallback((voucherCode: string | null) => setState((s) => ({ ...s, voucherCode })), []);

  const value = useMemo<CartValue>(
    () => ({
      ...state,
      channel,
      channels,
      count: state.lines.reduce((sum, l) => sum + l.quantity, 0),
      addLine,
      setQuantity,
      removeLine,
      clearLines,
      setChannel,
      setBranchId,
      updateDelivery,
      setVoucherCode,
    }),
    [state, channel, channels, addLine, setQuantity, removeLine, clearLines, setChannel, setBranchId, updateDelivery, setVoucherCode],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartValue => {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside <CartProvider>');
  return value;
};

/** What the quote / place endpoints take for each line. */
export const toLineInput = ({ productId, size, sugar, ice, toppingIds, quantity }: CartLine): CartLineInput => ({ productId, size, sugar, ice, toppingIds, quantity });
